import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Copy, Check, Loader2, Download, ArrowRight, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function CheckoutDrawer({ open, onClose, price = 27.9, anchor = 97.0 }) {
  const [step, setStep] = useState("form"); // form | pix | paid
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState("pending");
  const [downloadToken, setDownloadToken] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!order?.order_id || step === "paid") return;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/recipe/orders/status/${order.order_id}`);
        setStatus(data.status || "pending");
        if (data.paid && data.download_token) {
          setDownloadToken(data.download_token);
          setStep("paid");
          return;
        }
      } catch { /* ignore */ }
      pollRef.current = setTimeout(tick, 3500);
    };
    pollRef.current = setTimeout(tick, 3000);
    return () => clearTimeout(pollRef.current);
  }, [order, step]);

  const reset = () => {
    setStep("form"); setOrder(null); setDownloadToken(null);
    setStatus("pending"); setCopied(false);
  };
  const handleClose = () => { onClose(); setTimeout(reset, 400); };

  const createOrder = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Informe seu nome.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/recipe/orders`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
      });
      setOrder(data);
      setStep("pix");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao gerar Pix.");
    } finally {
      setLoading(false);
    }
  };

  const copyPix = async () => {
    const code = order?.pix?.copia_e_cola;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Pix copia-e-cola copiado");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Não consegui copiar."); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="overlay" data-testid="checkout-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }} onClick={handleClose}
            className="fixed inset-0 z-50 bg-black/40"
          />
          <motion.aside
            key="drawer" data-testid="checkout-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full md:w-[560px] bg-white border-l border-black overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-black px-6 md:px-8 py-4 flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.25em]">Checkout seguro · Pix</span>
              <button data-testid="checkout-close" onClick={handleClose} aria-label="Fechar" className="w-9 h-9 border border-black inline-flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                <X size={16} />
              </button>
            </div>

            <div className="px-6 md:px-8 py-8 md:py-10">
              <p className="text-xs uppercase tracking-[0.25em]">Edição I · The Quick Brownie</p>
              <h3 className="font-display text-3xl md:text-5xl tracking-[-0.025em] mt-3 leading-[0.95]">
                Receita completa<br />em PDF
              </h3>

              <div className="mt-8 flex items-baseline gap-4">
                <span className="text-base line-through opacity-50">{fmtBRL(anchor)}</span>
                <span className="font-display text-5xl md:text-6xl tracking-[-0.02em]">{fmtBRL(price)}</span>
              </div>
              <p className="text-[11px] uppercase tracking-[0.2em] mt-2">Pagamento único · Pix instantâneo</p>

              <div className="hairline mt-10" />

              {step === "form" && (
                <form data-testid="checkout-form" onSubmit={createOrder} className="mt-8 space-y-7">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="co-name">Seu nome</label>
                    <input id="co-name" data-testid="checkout-name" className="input-clean mt-2" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required placeholder="Como quer ser chamada" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="co-email">E-mail</label>
                    <input id="co-email" data-testid="checkout-email" type="email" required className="input-clean mt-2" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="pra receber o link" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="co-phone">WhatsApp (opcional)</label>
                    <input id="co-phone" data-testid="checkout-phone" className="input-clean mt-2" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(11) 99999-9999" />
                  </div>
                  <button type="submit" data-testid="checkout-submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
                    {loading ? "Gerando Pix..." : <>Gerar Pix <ArrowRight size={16} /></>}
                  </button>
                  <p className="text-[11px] leading-relaxed">
                    Pagamento processado pelo Mercado Pago. O PDF é liberado
                    automaticamente nesta tela em segundos após o Pix cair.
                  </p>
                </form>
              )}

              {step === "pix" && order && (
                <div data-testid="checkout-pix" className="mt-8 space-y-6">
                  <p className="text-sm">Escaneie o QR Code com seu banco ou copie o Pix:</p>
                  {order.pix?.qr_code_base64 ? (
                    <div className="border border-black p-4 bg-white">
                      <img
                        data-testid="checkout-qr"
                        src={`data:image/png;base64,${order.pix.qr_code_base64}`}
                        alt="QR Code Pix"
                        className="w-full h-auto max-w-[320px] mx-auto"
                      />
                    </div>
                  ) : null}

                  <button data-testid="checkout-copy-pix" onClick={copyPix} className="btn-outline w-full">
                    {copied ? <><Check size={16} /> Copiado</> : <><Copy size={16} /> Copiar Pix copia-e-cola</>}
                  </button>

                  <div className="border border-black p-4 flex items-center gap-3 text-sm">
                    <Loader2 size={16} className="animate-spin" />
                    <span data-testid="checkout-status">
                      Aguardando pagamento… {status !== "pending" && `(${status})`}
                    </span>
                  </div>

                  {order.pix?.ticket_url && (
                    <a href={order.pix.ticket_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] underline underline-offset-4">
                      <ExternalLink size={12} /> Abrir no app Mercado Pago
                    </a>
                  )}
                </div>
              )}

              {step === "paid" && downloadToken && (
                <div data-testid="checkout-paid" className="mt-8 space-y-6">
                  <div className="border border-black p-6">
                    <p className="text-xs uppercase tracking-[0.25em]">Pagamento confirmado</p>
                    <h4 className="font-display text-3xl md:text-4xl mt-2 tracking-[-0.02em] leading-tight">
                      Sua receita está liberada.
                    </h4>
                    <p className="mt-3 text-sm">
                      Guarde o link — ele continua ativo por 60 dias.
                    </p>
                  </div>
                  <a data-testid="checkout-download" href={`${API}/recipe/download/${downloadToken}`} download className="btn-primary w-full">
                    <Download size={16} /> Baixar o PDF agora
                  </a>
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
